import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

export interface Notification {
    id: string;
    title: string;
    message: string;
    timestamp: number;
    read: boolean;
    type: 'order_update' | 'info';
    orderId?: string;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    addNotification: (title: string, message: string, type?: 'order_update' | 'info', orderId?: string) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotifications: () => void;
    stopAlarm: () => void;
    isAlarming: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isAlarming, setIsAlarming] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const vibrationInterval = useRef<any>(null);

    useEffect(() => {
        if (currentUser?.uid) {
            const saved = localStorage.getItem(`yumii_notifications_${currentUser.uid}`);
            setNotifications(saved ? JSON.parse(saved) : []);
        } else {
            setNotifications([]);
        }
    }, [currentUser?.uid]);

    useEffect(() => {
        if (currentUser?.uid) {
            localStorage.setItem(`yumii_notifications_${currentUser.uid}`, JSON.stringify(notifications));
        }
    }, [notifications, currentUser?.uid]);

    const unreadCount = notifications.filter(n => !n.read).length;

    const addNotification = (title: string, message: string, type: 'order_update' | 'info' = 'info', orderId?: string) => {
        const newNotification: Notification = {
            id: Date.now().toString(),
            title,
            message,
            timestamp: Date.now(),
            read: false,
            type,
            orderId
        };
        setNotifications(prev => [newNotification, ...prev]);
    };

    const markAsRead = (id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const clearNotifications = () => {
        setNotifications([]);
    };

    const isAlarmingRef = useRef(isAlarming);
    useEffect(() => {
        isAlarmingRef.current = isAlarming;
    }, [isAlarming]);

    const stopAlarm = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        if (vibrationInterval.current) {
            clearInterval(vibrationInterval.current);
            vibrationInterval.current = null;
        }
        setIsAlarming(false);
    };

    const startAlarm = (soundType: 'restaurant' | 'driver') => {
        if (isAlarmingRef.current) return;

        const soundFile = soundType === 'driver' ? '/assets/sounds/driver_alert.mp3?v=3' : '/assets/sounds/restaurant_alert.mp3?v=3';
        console.log(`🎵 [ALARM] Starting audio: ${soundFile}`);
        
        setIsAlarming(true);
        isAlarmingRef.current = true; // Synchronous force

        if (audioRef.current) {
            audioRef.current.src = soundFile;
            // La balise DOM <audio> gère le loop nativement, mais on le force ici aussi par sécurité
            audioRef.current.loop = true;
            
            const playAudio = () => {
                if (audioRef.current && isAlarmingRef.current) {
                    audioRef.current.play().catch(err => {
                        console.warn('Audio play failed (waiting for interaction):', err);
                        setTimeout(playAudio, 2000);
                    });
                }
            };
            playAudio();
        }

        // Continuous Intense Vibration Pattern + Audio safety check
        if ('vibrate' in navigator) {
            const alarmPattern = [1000, 500, 1000, 500, 1000, 500];
            
            vibrationInterval.current = setInterval(() => {
                navigator.vibrate(alarmPattern);
                // Aggressive check: if audio was paused by OS (e.g. FCM ding interrupted it), restart it
                if (isAlarmingRef.current && audioRef.current) {
                    if (audioRef.current.paused || audioRef.current.ended || audioRef.current.currentTime === 0) {
                        audioRef.current.play().catch(() => {});
                    }
                }
            }, 4000); 
            
            navigator.vibrate(alarmPattern);
        }
    };

    useEffect(() => {
        const handleNotification = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { title, message, type, orderId, data } = customEvent.detail;
            console.log('[DEBUG] Internal Notification Event:', customEvent.detail);

            addNotification(title, message, type, orderId);

            // Handle Alarm triggers based on role and type
            // isOrder: Strictly new orders or nudges for restaurateurs
            const isNewOrder = data?.type === 'new_order' || type === 'new_order';
            const isNudge = data?.type === 'nudge' || type === 'nudge';
            
            // isMission: Strictly new assignments for drivers
            const isNewMission = data?.type === 'driver_assigned' || data?.type === 'new_mission' || type === 'new_mission';

            // Handle Alarm triggers based on role and type
            if (currentUser?.role === 'restaurateur' && (isNewOrder || isNudge)) {
                console.log('🔔 [ALARM] Triggering for Restaurateur', { isNewOrder, isNudge });
                startAlarm('restaurant');
            } else if (currentUser?.role === 'livreur' && isNewMission) {
                console.log('🔔 [ALARM] Triggering for Livreur', { isNewMission, data_type: data?.type, event_type: type });
                startAlarm('driver');
            }

            // Global stop signal (e.g. mission taken by another)
            if (data?.type === 'mission_taken' || data?.type === 'cancel_alarm') {
                stopAlarm();
            }
        };

        window.addEventListener('yumii:notification', handleNotification);

        return () => {
            window.removeEventListener('yumii:notification', handleNotification);
        };
    }, []);

    return (
        <NotificationContext.Provider value={{ 
            notifications, 
            unreadCount, 
            addNotification, 
            markAsRead, 
            markAllAsRead, 
            clearNotifications,
            stopAlarm,
            isAlarming
        }}>
            {children}
            {/* Balise DOM cachée pour garantir un fonctionnement stable du loop en WebView Android */}
            <audio ref={audioRef as any} preload="auto" loop />
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
